import React, { useState, useEffect } from 'react';
import './ManageNotice.css';
import collegeConfig from '../../config/collegeConfig';
import {
  FaPlus,
  FaEdit,
  FaTrash,
  FaEye,
  FaBell,
  FaBullhorn,
  FaCalendarAlt,
  FaClock,
  FaSearch,
  FaTimes,
  FaSave,
  FaUserGraduate,
  FaChalkboardTeacher,
  FaExclamationCircle,
  FaInfoCircle,
  FaCheckCircle,
  FaPaperclip,
  FaFilter,
  FaSort
} from 'react-icons/fa';

const ManageNotice = () => {
  const [notices, setNotices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('create'); // 'create', 'edit', 'view'
  const [selectedNotice, setSelectedNotice] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterPriority, setFilterPriority] = useState('all');
  const [sortBy, setSortBy] = useState('newest');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Form state
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    type: 'general',
    targetAudience: [],
    priority: 'medium',
    publishDate: '',
    expiryDate: '',
    attachments: []
  });

  useEffect(() => {
    fetchNotices();
  }, []);

  const fetchNotices = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${collegeConfig.api.baseURL}/notices`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setNotices(data.notices || []);
      } else {
        setError('Failed to fetch notices');
      }
    } catch (error) {
      console.error('Error fetching notices:', error);
      setError('Error connecting to server');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    // Validate target audience
    if (formData.targetAudience.length === 0) {
      setError('Please select at least one target audience');
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      const url = modalMode === 'create' 
        ? `${collegeConfig.api.baseURL}/notices`
        : `${collegeConfig.api.baseURL}/notices/${selectedNotice._id}`;
      
      const method = modalMode === 'create' ? 'POST' : 'PUT';
      
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });
      
      const data = await response.json();
      
      if (response.ok) {
        setSuccess(data.message);
        fetchNotices();
        closeModal();
      } else {
        setError(data.message || 'Operation failed');
      }
    } catch (error) {
      console.error('Error saving notice:', error);
      setError('Error connecting to server');
    }
  };

  const handleDelete = async (noticeId) => {
    if (!window.confirm('Are you sure you want to delete this notice?')) {
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${collegeConfig.api.baseURL}/notices/${noticeId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        setSuccess('Notice deleted successfully');
        fetchNotices();
      } else {
        setError('Failed to delete notice');
      }
    } catch (error) {
      console.error('Error deleting notice:', error);
      setError('Error connecting to server');
    }
  };

  const handleTargetAudienceChange = (target) => {
    const currentTargets = formData.targetAudience;
    let newTargets;
    
    if (currentTargets.includes(target)) {
      newTargets = currentTargets.filter(t => t !== target);
    } else {
      newTargets = [...currentTargets, target];
    }
    
    setFormData({ ...formData, targetAudience: newTargets });
  };

  const openModal = (mode, notice = null) => {
    setModalMode(mode);
    setSelectedNotice(notice);
    setError('');
    
    if (mode === 'create') {
      setFormData({
        title: '',
        content: '',
        type: 'general',
        targetAudience: [],
        priority: 'medium',
        publishDate: '',
        expiryDate: '',
        attachments: []
      });
    } else if (notice) {
      setFormData({
        title: notice.title,
        content: notice.content,
        type: notice.type,
        targetAudience: notice.targetAudience,
        priority: notice.priority,
        publishDate: notice.publishDate ? 
          new Date(notice.publishDate).toISOString().split('T')[0] : '',
        expiryDate: notice.expiryDate ? 
          new Date(notice.expiryDate).toISOString().split('T')[0] : '',
        attachments: notice.attachments || []
      });
    }
    
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedNotice(null);
    setFormData({
      title: '',
      content: '',
      type: 'general',
      targetAudience: [],
      priority: 'medium',
      publishDate: '',
      expiryDate: '',
      attachments: []
    });
    setError('');
  };

  // Filter and sort notices
  const filteredNotices = notices.filter(notice => {
    // Search filter
    const matchesSearch = notice.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         notice.content.toLowerCase().includes(searchTerm.toLowerCase());
    
    // Type filter
    const matchesType = filterType === 'all' || notice.type === filterType;
    
    // Priority filter
    const matchesPriority = filterPriority === 'all' || notice.priority === filterPriority;
    
    return matchesSearch && matchesType && matchesPriority;
  });

  // Sort notices
  const sortedNotices = [...filteredNotices].sort((a, b) => {
    switch (sortBy) {
      case 'newest':
        return new Date(b.createdAt) - new Date(a.createdAt);
      case 'oldest':
        return new Date(a.createdAt) - new Date(b.createdAt);
      case 'priority':
        const priorityOrder = { high: 3, medium: 2, low: 1 };
        return priorityOrder[b.priority] - priorityOrder[a.priority];
      default:
        return 0;
    }
  });

  // Get priority color
  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high':
        return '#e74c3c';
      case 'medium':
        return '#f39c12';
      case 'low':
        return '#95a5a6';
      default:
        return '#95a5a6';
    }
  };

  // Get type icon
  const getTypeIcon = (type) => {
    switch (type) {
      case 'urgent':
        return <FaExclamationCircle />;
      case 'announcement':
        return <FaBullhorn />;
      case 'academic':
        return <FaUserGraduate />;
      case 'event':
        return <FaCalendarAlt />;
      default:
        return <FaInfoCircle />;
    }
  };

  // Format date
  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Check if notice is expired
  const isExpired = (expiryDate) => {
    if (!expiryDate) return false;
    return new Date(expiryDate) < new Date();
  };

  // Clear success message after 3 seconds
  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(''), 3000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  if (loading) {
    return (
      <div className="manage-notice-loading">
        <div className="spinner"></div>
        <p>Loading notices...</p>
      </div>
    );
  }

  return (
    <div className="manage-notice-container">
      {/* Header Section */}
      <div className="manage-notice-header">
        <div className="header-content">
          <h2>Manage Notices</h2>
          <p>Create and manage announcements for students and teachers</p>
        </div>
        <button 
          className="btn-create-notice"
          onClick={() => openModal('create')}
        >
          <FaPlus /> Create New Notice
        </button>
      </div>

      {/* Success/Error Messages */}
      {success && <div className="alert alert-success"><FaCheckCircle /> {success}</div>}
      {error && <div className="alert alert-error"><FaExclamationCircle /> {error}</div>}

      {/* Controls Section */}
      <div className="notice-controls">
        <div className="search-bar">
          <FaSearch className="search-icon" />
          <input
            type="text"
            placeholder="Search notices..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        
        <div className="filters">
          <div className="filter-group">
            <FaFilter className="filter-icon" />
            <select 
              value={filterType} 
              onChange={(e) => setFilterType(e.target.value)}
              className="filter-select"
            >
              <option value="all">All Types</option>
              <option value="general">General</option>
              <option value="urgent">Urgent</option>
              <option value="announcement">Announcement</option>
              <option value="academic">Academic</option>
              <option value="event">Event</option>
            </select>
          </div>
          
          <div className="filter-group">
            <FaExclamationCircle className="filter-icon" />
            <select 
              value={filterPriority} 
              onChange={(e) => setFilterPriority(e.target.value)}
              className="filter-select"
            >
              <option value="all">All Priorities</option>
              <option value="high">High</option>
              <option value="medium">Medium</option>
              <option value="low">Low</option>
            </select>
          </div>
          
          <div className="filter-group">
            <FaSort className="filter-icon" />
            <select 
              value={sortBy} 
              onChange={(e) => setSortBy(e.target.value)}
              className="filter-select"
            >
              <option value="newest">Newest First</option>
              <option value="oldest">Oldest First</option>
              <option value="priority">By Priority</option>
            </select>
          </div>
        </div>
        
        <div className="notice-stats">
          <div className="stat-badge">
            <FaBell />
            <span>{notices.length} Total</span>
          </div>
          <div className="stat-badge">
            <FaExclamationCircle />
            <span>{notices.filter(n => n.priority === 'high').length} Urgent</span>
          </div>
        </div>
      </div>

      {/* Notices List */}
      <div className="notices-list">
        {sortedNotices.length === 0 ? (
          <div className="no-notices">
            <FaBell className="no-notices-icon" />
            <h3>No notices found</h3>
            <p>Create your first notice to get started</p>
          </div>
        ) : (
          sortedNotices.map(notice => (
            <div 
              key={notice._id} 
              className={`notice-card ${isExpired(notice.expiryDate) ? 'expired' : ''}`}
            >
              <div className="notice-card-header">
                <div className="notice-type-icon" style={{ color: getPriorityColor(notice.priority) }}>
                  {getTypeIcon(notice.type)}
                </div>
                <div className="notice-info">
                  <h3>{notice.title}</h3>
                  <div className="notice-meta">
                    <span className="notice-type">{notice.type}</span>
                    <span className={`notice-priority priority-${notice.priority}`}>
                      {notice.priority} priority
                    </span>
                    <span className="notice-date">
                      <FaCalendarAlt /> {formatDate(notice.publishDate)}
                    </span>
                    {notice.expiryDate && (
                      <span className={`notice-expiry ${isExpired(notice.expiryDate) ? 'expired' : ''}`}>
                        <FaClock /> Expires: {formatDate(notice.expiryDate)}
                      </span>
                    )}
                  </div>
                </div>
                <div className="notice-actions">
                  <button 
                    className="btn-icon btn-view"
                    onClick={() => openModal('view', notice)}
                    title="View Details"
                  >
                    <FaEye />
                  </button>
                  <button 
                    className="btn-icon btn-edit"
                    onClick={() => openModal('edit', notice)}
                    title="Edit Notice"
                  >
                    <FaEdit />
                  </button>
                  <button 
                    className="btn-icon btn-delete"
                    onClick={() => handleDelete(notice._id)}
                    title="Delete Notice"
                  >
                    <FaTrash />
                  </button>
                </div>
              </div>
              
              <div className="notice-card-body">
                <p className="notice-content">
                  {notice.content.length > 150 
                    ? notice.content.substring(0, 150) + '...' 
                    : notice.content}
                </p>
                
                <div className="notice-footer">
                  <div className="notice-targets">
                    {notice.targetAudience.includes('students') && (
                      <span className="target-badge students">
                        <FaUserGraduate /> Students
                      </span>
                    )}
                    {notice.targetAudience.includes('teachers') && (
                      <span className="target-badge teachers">
                        <FaChalkboardTeacher /> Teachers
                      </span>
                    )}
                  </div>
                  
                  <div className="notice-stats-info">
                    <span className="view-count">
                      <FaEye /> {notice.viewCount || 0} views
                    </span>
                    {notice.attachments && notice.attachments.length > 0 && (
                      <span className="attachment-count">
                        <FaPaperclip /> {notice.attachments.length}
                      </span>
                    )}
                  </div>
                  
                  <div className="notice-author">
                    By {notice.createdBy?.profile?.firstName} {notice.createdBy?.profile?.lastName}
                  </div>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content large" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>
                {modalMode === 'create' ? 'Create New Notice' : 
                 modalMode === 'edit' ? 'Edit Notice' : 'Notice Details'}
              </h3>
              <button className="modal-close" onClick={closeModal}>
                <FaTimes />
              </button>
            </div>
            
            {modalMode === 'view' ? (
              <div className="notice-view">
                <div className="view-header">
                  <h2>{selectedNotice.title}</h2>
                  <div className="view-meta">
                    <span className="notice-type">{selectedNotice.type}</span>
                    <span className={`notice-priority priority-${selectedNotice.priority}`}>
                      {selectedNotice.priority} priority
                    </span>
                  </div>
                </div>
                
                <div className="view-content">
                  <div className="content-section">
                    <h4>Content</h4>
                    <p>{selectedNotice.content}</p>
                  </div>
                  
                  <div className="content-section">
                    <h4>Target Audience</h4>
                    <div className="notice-targets">
                      {selectedNotice.targetAudience.includes('students') && (
                        <span className="target-badge students">
                          <FaUserGraduate /> Students
                        </span>
                      )}
                      {selectedNotice.targetAudience.includes('teachers') && (
                        <span className="target-badge teachers">
                          <FaChalkboardTeacher /> Teachers
                        </span>
                      )}
                    </div>
                  </div>
                  
                  <div className="content-section">
                    <h4>Details</h4>
                    <div className="detail-grid">
                      <div className="detail-item">
                        <strong>Published:</strong> {formatDate(selectedNotice.publishDate)}
                      </div>
                      {selectedNotice.expiryDate && (
                        <div className="detail-item">
                          <strong>Expires:</strong> {formatDate(selectedNotice.expiryDate)}
                        </div>
                      )}
                      <div className="detail-item">
                        <strong>Views:</strong> {selectedNotice.viewCount || 0}
                      </div>
                      <div className="detail-item">
                        <strong>Created by:</strong> {selectedNotice.createdBy?.profile?.firstName} {selectedNotice.createdBy?.profile?.lastName}
                      </div>
                    </div>
                  </div>
                  
                  {selectedNotice.attachments && selectedNotice.attachments.length > 0 && (
                    <div className="content-section">
                      <h4>Attachments</h4>
                      <div className="attachments-list">
                        {selectedNotice.attachments.map((attachment, index) => (
                          <div key={index} className="attachment-item">
                            <FaPaperclip /> {attachment.fileName}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="notice-form">
                <div className="form-row">
                  <div className="form-group full-width">
                    <label>Notice Title *</label>
                    <input
                      type="text"
                      value={formData.title}
                      onChange={(e) => setFormData({...formData, title: e.target.value})}
                      placeholder="Enter notice title"
                      required
                      disabled={modalMode === 'view'}
                    />
                  </div>
                </div>
                
                <div className="form-row">
                  <div className="form-group">
                    <label>Notice Type *</label>
                    <select
                      value={formData.type}
                      onChange={(e) => setFormData({...formData, type: e.target.value})}
                      disabled={modalMode === 'view'}
                    >
                      <option value="general">General</option>
                      <option value="urgent">Urgent</option>
                      <option value="announcement">Announcement</option>
                      <option value="academic">Academic</option>
                      <option value="event">Event</option>
                    </select>
                  </div>
                  
                  <div className="form-group">
                    <label>Priority *</label>
                    <select
                      value={formData.priority}
                      onChange={(e) => setFormData({...formData, priority: e.target.value})}
                      disabled={modalMode === 'view'}
                    >
                      <option value="low">Low</option>
                      <option value="medium">Medium</option>
                      <option value="high">High</option>
                    </select>
                  </div>
                </div>
                
                <div className="form-group">
                  <label>Target Audience * (Select at least one)</label>
                  <div className="checkbox-group">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={formData.targetAudience.includes('students')}
                        onChange={() => handleTargetAudienceChange('students')}
                        disabled={modalMode === 'view'}
                      />
                      <span className="checkbox-text">
                        <FaUserGraduate /> Students
                      </span>
                    </label>
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={formData.targetAudience.includes('teachers')}
                        onChange={() => handleTargetAudienceChange('teachers')}
                        disabled={modalMode === 'view'}
                      />
                      <span className="checkbox-text">
                        <FaChalkboardTeacher /> Teachers
                      </span>
                    </label>
                  </div>
                </div>
                
                <div className="form-group">
                  <label>Content *</label>
                  <textarea
                    value={formData.content}
                    onChange={(e) => setFormData({...formData, content: e.target.value})}
                    placeholder="Enter notice content..."
                    rows="8"
                    required
                    disabled={modalMode === 'view'}
                  />
                </div>
                
                <div className="form-row">
                  <div className="form-group">
                    <label>Publish Date</label>
                    <input
                      type="date"
                      value={formData.publishDate}
                      onChange={(e) => setFormData({...formData, publishDate: e.target.value})}
                      disabled={modalMode === 'view'}
                    />
                  </div>
                  
                  <div className="form-group">
                    <label>Expiry Date (Optional)</label>
                    <input
                      type="date"
                      value={formData.expiryDate}
                      onChange={(e) => setFormData({...formData, expiryDate: e.target.value})}
                      disabled={modalMode === 'view'}
                    />
                  </div>
                </div>
                
                {modalMode !== 'view' && (
                  <div className="form-actions">
                    <button type="submit" className="btn-primary">
                      <FaSave /> {modalMode === 'create' ? 'Create Notice' : 'Update Notice'}
                    </button>
                    <button type="button" className="btn-secondary" onClick={closeModal}>
                      Cancel
                    </button>
                  </div>
                )}
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ManageNotice;